/**
 * The recording transport's rules (`features/call-audio.tsx`). Pure, for
 * `test/recording-playback.test.ts`.
 *
 * The audio element plays the file; these decide what the controls around it ask of it. Where
 * a pointer on the bar puts the playhead, where a key on it does, when the transcript under it
 * scrolls to keep the line being spoken in view, and how a file's size reads. Each has a wrong
 * answer that looks right, which is why they are written down here and not at the call site.
 */

/** How far one arrow key moves the playhead. Five seconds is about one turn of a conversation:
 *  smaller and finding a moment takes twenty presses, larger and it skips the moment. */
export const SEEK_STEP_S = 5;

const clamp = (value: number, max: number) => Math.min(max, Math.max(0, value));

/**
 * The position a pointer `offsetX` along a bar `width` wide asks for, in seconds of `length`.
 *
 * Clamped rather than refused: a drag that runs past either end of the bar means that end,
 * which is what every player does. A bar with no width, or a file with no length yet, has
 * nowhere to go and answers the start.
 */
export function seekAt(offsetX: number, width: number, length: number): number {
  if (!(width > 0) || !(length > 0)) return 0;
  return clamp(offsetX / width, 1) * length;
}

/**
 * Where a key on the focused bar moves the playhead from `current`, or null for a key that is
 * not a seek — so the caller lets it through rather than swallowing it.
 *
 * Arrows nudge by `SEEK_STEP_S` and Home and End jump, which is the slider pattern a screen
 * reader expects. Up and Down mean the same as Right and Left, as they do on a native range.
 */
export function seekByKey(key: string, current: number, length: number): number | null {
  switch (key) {
    case "ArrowRight":
    case "ArrowUp":
      return clamp(current + SEEK_STEP_S, length);
    case "ArrowLeft":
    case "ArrowDown":
      return clamp(current - SEEK_STEP_S, length);
    case "Home":
      return 0;
    case "End":
      return length;
    default:
      return null;
  }
}

/**
 * The `scrollTop` that brings the line being spoken into a list's view, or null when it is
 * already in view.
 *
 * `line` is measured against the list's scrolled content (`offsetTop`, with the list as the
 * offset parent). A line that fits is left alone: scrolling on every line change would move
 * the text under a reader who is keeping up. A line outside the view lands a third of the way
 * down rather than at the top, so the lines about to be spoken are on screen below it.
 */
export function followScrollTop(
  list: { scrollTop: number; clientHeight: number },
  line: { offsetTop: number; offsetHeight: number },
): number | null {
  const top = line.offsetTop;
  const bottom = top + line.offsetHeight;
  if (top >= list.scrollTop && bottom <= list.scrollTop + list.clientHeight) return null;
  return Math.max(0, Math.round(top - list.clientHeight / 3));
}

/** A recording's size as a person reads it: `11.6 MB` for a call, `340 KB` for a short one.
 *  Never `0 KB` — a file that exists has a size. */
export function fileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
